import { useRef, useState } from 'react';
import { Button, message } from '../ui';
import { Camera } from 'lucide-react';
import { api, request } from '../api/client';
import { getDeviceFix } from '../hooks/useDeviceFix';
import { formatIst } from '../lib/time';
import { PALETTE } from '../lib/palette';

/**
 * "Map camera" for a field visit: take/pick a photo, capture the device GPS + time, STAMP them onto the
 * image (a tamper-evident watermark), compress, upload, and attach to the visit. Location is best-effort
 * — a photo with no fix is still allowed (flagged server-side); a mock GPS is rejected by the server.
 * On Android the native geo-camera can do the stamping; this is the web equivalent.
 */
export function VisitPhotoCapture({ visitId, onAdded, disabled, label = 'Add photos', size = 'md', remaining = 10 }: {
  visitId: string; onAdded?: () => void; disabled?: boolean; label?: string; size?: 'sm' | 'md' | 'lg'; remaining?: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');

  // Upload one file (device fix + reverse-geocode + watermark + attach). Returns true on success.
  const uploadOne = async (
    file: File,
    fix: { lat: number; lng: number; accuracyM: number | null; isMockLocation: boolean } | null,
    address: string | null,
  ): Promise<boolean> => {
    const takenAt = new Date().toISOString();
    try {
      const blob = await stampAndCompress(file, { lat: fix?.lat ?? null, lng: fix?.lng ?? null, accuracyM: fix?.accuracyM ?? null, takenAt, address });
      const form = new FormData();
      form.append('file', new File([blob], `visit-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      const up = await api.post('/uploads/visit-photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = (up.data?.data ?? up.data)?.url;
      if (!url) throw new Error('Upload failed');
      await request({
        url: `/visits/${visitId}/photos`, method: 'POST',
        data: { url, lat: fix?.lat, lng: fix?.lng, accuracyM: fix?.accuracyM, isMockLocation: fix?.isMockLocation ?? false, takenAt },
      });
      return true;
    } catch (e: any) {
      // Surface the first meaningful error but keep going through the batch.
      message.error(e?.response?.data?.error?.message ?? e?.message ?? 'A photo failed to upload');
      return false;
    }
  };

  const handle = async (fileList: FileList) => {
    const all = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (!all.length) return;
    const files = all.slice(0, Math.max(0, remaining));
    const skipped = all.length - files.length;
    setBusy(true);
    // ONE device fix + one reverse-geocode for the whole batch — they're taken here, now.
    let fix: { lat: number; lng: number; accuracyM: number | null; isMockLocation: boolean } | null = null;
    try { fix = await getDeviceFix(); } catch { fix = null; } // no-GPS allowed → flagged, not blocked
    let address: string | null = null;
    if (fix) {
      try {
        const r: any = await Promise.race([
          request<any>({ url: '/geo/reverse', params: { lat: fix.lat, lng: fix.lng } }),
          new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 2500)),
        ]);
        address = typeof r === 'string' ? r : (r?.address ?? r?.display_name ?? r?.formatted ?? null);
      } catch { address = null; }
    }
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress(files.length > 1 ? `${i + 1}/${files.length}` : '');
      if (await uploadOne(files[i], fix, address)) ok++;
    }
    setBusy(false); setProgress('');
    if (inputRef.current) inputRef.current.value = '';
    if (ok > 0) {
      message.success(`${ok} photo${ok === 1 ? '' : 's'} added${fix ? ' — location captured' : ' (no location — flagged)'}` + (skipped ? ` · ${skipped} skipped (10-photo limit)` : ''));
      onAdded?.();
    } else if (skipped && !files.length) {
      message.warning('This visit already has the maximum 10 photos.');
    }
  };

  return (
    <>
      {/* `multiple` lets the rep pick several at once (or take one via the picker's Camera option).
          The device fix at upload time is what geotags them — not the file's EXIF. */}
      <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.length) handle(e.target.files); }} />
      <Button icon={<Camera size={16} />} loading={busy} disabled={disabled || remaining <= 0} size={size} onClick={() => inputRef.current?.click()}>
        {busy && progress ? `Uploading ${progress}…` : label}
      </Button>
    </>
  );
}

// ── Watermark + compress ─────────────────────────────────────────────────────
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('Could not read the image'));
    img.src = URL.createObjectURL(file);
  });
}

async function stampAndCompress(
  file: File,
  meta: { lat: number | null; lng: number | null; accuracyM: number | null; takenAt: string; address: string | null },
): Promise<Blob> {
  const img = await loadImage(file);
  const maxDim = 1600; // cap the long edge → smaller upload, still crisp
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) { URL.revokeObjectURL(img.src); throw new Error('Canvas unavailable'); }
  ctx.drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);

  const lines = [
    meta.lat != null && meta.lng != null
      ? `GPS  ${meta.lat.toFixed(5)}, ${meta.lng.toFixed(5)}${meta.accuracyM != null ? `   ±${meta.accuracyM} m` : ''}`
      : 'GPS  location not captured',
    `TIME  ${formatIst(meta.takenAt)}`,
    ...(meta.address ? [meta.address.length > 64 ? meta.address.slice(0, 61) + '…' : meta.address] : []),
  ];
  drawFooter(ctx, w, h, lines);

  return await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error('Could not encode image'))), 'image/jpeg', 0.82));
}

function drawFooter(ctx: CanvasRenderingContext2D, w: number, h: number, lines: string[]) {
  const pad = Math.round(w * 0.022);
  const fontSize = Math.max(13, Math.round(w * 0.026));
  ctx.font = `600 ${fontSize}px system-ui, -apple-system, Segoe UI, sans-serif`;
  ctx.textBaseline = 'top';
  const lineH = Math.round(fontSize * 1.4);
  const barH = lineH * lines.length + pad * 2;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, h - barH, w, barH);
  // subtle green accent bar (brand) on the left of the stamp
  ctx.fillStyle = PALETTE.brand[600];
  ctx.fillRect(0, h - barH, Math.max(3, Math.round(w * 0.006)), barH);
  ctx.fillStyle = '#fff';
  lines.forEach((ln, i) => ctx.fillText(ln, pad + Math.round(w * 0.012), h - barH + pad + i * lineH, w - pad * 2));
}
